import React, { useEffect, useRef, useState } from 'react'
import { useUserTimeZone } from '@ztechapps/common.hook'
import styles from './gantt.module.scss'
import type { GanttChartProps, ViewMode } from './types'
import { TaskList } from './TaskList'
import { Timeline } from './Timeline'
import { useTimeline } from './hooks/useTimeline'

export const GanttChart = (props: GanttChartProps) => {
  const userTz = useUserTimeZone()
  const tz = props.timezone || userTz || 'UTC'

  const view: ViewMode = props.view || 'month'
  const rowHeight = props.rowHeight ?? 28
  const barHeight = props.barHeight ?? 18
  const columnWidth = props.columnWidth ?? 28
  const showWeekends = props.showWeekends ?? true

  const [collapsed, setCollapsed] = useState<string[]>(props.collapsedIds || [])
  useEffect(() => {
    if (props.collapsedIds) setCollapsed(props.collapsedIds)
  }, [props.collapsedIds])

  const timeline = useTimeline({
    tasks: props.tasks,
    view,
    timezone: tz,
    startDate: props.startDate,
    endDate: props.endDate,
    columnWidth,
    showWeekends,
    defaultView: 'month',
    defaultColumnWidth: 28,
  })

  const leftBodyRef = useRef<HTMLDivElement>(null)
  const rightHeaderRef = useRef<HTMLDivElement>(null)
  const rightBodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const left = leftBodyRef.current
    const right = rightBodyRef.current
    const header = rightHeaderRef.current
    if (!left || !right || !header) return

    let syncing = false
    const onRightScroll = () => {
      if (syncing) return
      syncing = true
      left.scrollTop = right.scrollTop
      header.scrollLeft = right.scrollLeft
      syncing = false
    }
    const onLeftScroll = () => {
      if (syncing) return
      syncing = true
      right.scrollTop = left.scrollTop
      syncing = false
    }

    right.addEventListener('scroll', onRightScroll)
    left.addEventListener('scroll', onLeftScroll)
    return () => {
      right.removeEventListener('scroll', onRightScroll)
      left.removeEventListener('scroll', onLeftScroll)
    }
  }, [])

  const handleToggleCollapse = (id: string, collapsedNow: boolean) => {
    setCollapsed((prev) => {
      const s = new Set(prev)
      if (collapsedNow) s.add(id)
      else s.delete(id)
      const list = Array.from(s)
      props.onToggleCollapse?.(id, collapsedNow)
      return list
    })
  }

  return (
    <div className={styles.container} style={props.style}>
      <TaskList
        tasks={props.tasks}
        tz={tz}
        rowHeight={rowHeight}
        collapsedIds={collapsed}
        onToggleCollapse={handleToggleCollapse}
        onRowClick={props.onTaskClick}
        bodyRef={leftBodyRef}
      />
      <Timeline
        tasks={props.tasks}
        tz={tz}
        rowHeight={rowHeight}
        barHeight={barHeight}
        collapsedIds={collapsed}
        timeline={timeline}
        onTaskClick={props.onTaskClick}
        headerRef={rightHeaderRef}
        bodyRef={rightBodyRef}
      />
    </div>
  )
}

export default GanttChart
